import { Archive, Inbox, Mail, MailOpen, Plus, RefreshCw, Reply, Send, SendHorizontal, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SurfaceCard } from '../../components/ui/SurfaceCard';
import { emailApi, type EmailRecord, type JMAPMailbox } from './emailApi';

const FOLDER_ICONS: Record<string, React.ElementType> = {
	inbox: Inbox,
	sent: SendHorizontal,
	drafts: Archive,
	trash: Trash2,
	archive: Archive
};

function FolderIcon({ role }: { role: string }) {
	const Icon = FOLDER_ICONS[role?.toLowerCase()] ?? Mail;
	return <Icon className="h-4 w-4" />;
}

function formatDate(dateStr: string | undefined) {
	if (!dateStr) return '';
	const d = new Date(dateStr);
	if (isNaN(d.getTime())) return dateStr;
	const now = new Date();
	const diffMs = now.getTime() - d.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	if (diffDays === 0) {
		return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}
	if (diffDays < 7) {
		return d.toLocaleDateString([], { weekday: 'short' });
	}
	return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function extractDisplayName(addr: string | undefined) {
	if (!addr) return 'Unknown';
	const match = addr.match(/^(.+?)\s*<.+>$/);
	return match ? match[1].trim() : addr;
}

function extractEmail(addr: string | undefined) {
	if (!addr) return '';
	const match = addr.match(/<(.+?)>/);
	return match ? match[1] : addr;
}

export function EmailPage() {
	const queryClient = useQueryClient();
	const [selectedFolder, setSelectedFolder] = useState('inbox');
	const [selectedEmail, setSelectedEmail] = useState<EmailRecord | null>(null);
	const [composeOpen, setComposeOpen] = useState(false);
	const [replyOpen, setReplyOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<'live' | 'stored'>('live');

	const [compose, setCompose] = useState({
		to: '',
		cc: '',
		subject: '',
		bodyText: '',
		bodyHTML: ''
	});

	// JMAP mailboxes
	const mailboxesQuery = useQuery({
		queryKey: ['email-mailboxes'],
		queryFn: emailApi.listMailboxes,
		staleTime: 60000
	});

	// Live JMAP emails for selected folder
	const liveEmailsQuery = useQuery({
		queryKey: ['email-live', selectedFolder],
		queryFn: () => emailApi.fetchMailbox(selectedFolder, 50),
		enabled: activeTab === 'live',
		staleTime: 30000
	});

	// Stored DB emails
	const storedEmailsQuery = useQuery({
		queryKey: ['email-stored'],
		queryFn: () => emailApi.listStored(),
		enabled: activeTab === 'stored',
		staleTime: 30000
	});

	const sendMutation = useMutation({
		mutationFn: (payload: Parameters<typeof emailApi.send>[0]) => emailApi.send(payload),
		onSuccess: () => {
			toast.success('Email sent');
			setComposeOpen(false);
			setReplyOpen(false);
			setCompose({ to: '', cc: '', subject: '', bodyText: '', bodyHTML: '' });
			void queryClient.invalidateQueries({ queryKey: ['email-live'] });
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Failed to send email');
		}
	});

	const mailboxes: JMAPMailbox[] = Array.isArray(mailboxesQuery.data) ? mailboxesQuery.data : [];

	const currentEmails: EmailRecord[] = useMemo(() => {
		if (activeTab === 'live') {
			return Array.isArray(liveEmailsQuery.data) ? liveEmailsQuery.data : [];
		}
		return Array.isArray(storedEmailsQuery.data) ? storedEmailsQuery.data : [];
	}, [activeTab, liveEmailsQuery.data, storedEmailsQuery.data]);

	// Pre-fill reply form when an email is selected
	useEffect(() => {
		if (replyOpen && selectedEmail) {
			const rawFrom = selectedEmail.from ?? selectedEmail.from_addr;
			let fromEmail: string;
			if (Array.isArray(rawFrom)) {
				fromEmail = (rawFrom[0] as { email?: string })?.email ?? '';
			} else if (typeof rawFrom === 'object' && rawFrom !== null) {
				fromEmail = (rawFrom as { email?: string }).email ?? '';
			} else {
				fromEmail = extractEmail(rawFrom as string | undefined);
			}
			const subject = selectedEmail.subject ?? '';
			setCompose((prev) => ({
				...prev,
				to: fromEmail,
				subject: subject.startsWith('Re:') ? subject : `Re: ${subject}`,
				bodyText: ''
			}));
		}
	}, [replyOpen, selectedEmail]);

	function handleSend() {
		const to = compose.to
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);
		const cc = compose.cc
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);

		if (to.length === 0) {
			toast.error('At least one recipient is required');
			return;
		}
		if (!compose.subject.trim()) {
			toast.error('Subject is required');
			return;
		}
		sendMutation.mutate({
			to,
			cc,
			subject: compose.subject,
			body_text: compose.bodyText,
			body_html: compose.bodyHTML || undefined
		});
	}

	const isLoading = activeTab === 'live' ? liveEmailsQuery.isLoading : storedEmailsQuery.isLoading;

	return (
		<div className="space-y-3">
			<section className="rounded-2xl border border-subtle bg-gradient-to-br from-surface to-surface-2 px-5 py-3 shadow-panel backdrop-blur-xl">
				<div className="flex items-center justify-between">
					<h1 className="text-2xl font-semibold text-primary">Email</h1>
					<button
						onClick={() => setComposeOpen(true)}
						className="inline-flex items-center gap-1.5 btn-primary transition hover:opacity-90"
					>
						<Plus className="h-4 w-4" />
						Compose
					</button>
				</div>
			</section>

			<div className="grid gap-3 lg:grid-cols-[200px_minmax(0,1fr)]">
				{/* Sidebar: folders */}
				<div className="space-y-3">
					<SurfaceCard title="Folders">
						<div className="space-y-1">
							{mailboxes.length === 0 ? (
								<>
									{['inbox', 'sent', 'drafts', 'trash'].map((role) => (
										<button
											key={role}
											onClick={() => {
												setSelectedFolder(role);
												setActiveTab('live');
												setSelectedEmail(null);
											}}
											className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-sm transition ${
												selectedFolder === role && activeTab === 'live'
													? 'bg-accent text-accent-contrast font-semibold'
													: 'text-secondary hover:bg-surface-2 hover:text-primary'
											}`}
										>
											<FolderIcon role={role} />
											<span className="capitalize">{role}</span>
										</button>
									))}
								</>
							) : (
								mailboxes.map((mb) => (
									<button
										key={mb.id}
										onClick={() => {
											setSelectedFolder(mb.role || mb.name.toLowerCase());
											setActiveTab('live');
											setSelectedEmail(null);
										}}
										className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-sm transition ${
											selectedFolder === (mb.role || mb.name.toLowerCase()) && activeTab === 'live'
												? 'bg-accent text-accent-contrast font-semibold'
												: 'text-secondary hover:bg-surface-2 hover:text-primary'
										}`}
									>
										<FolderIcon role={mb.role || mb.name.toLowerCase()} />
										<span className="flex-1 text-left">{mb.name}</span>
										{mb.unreadEmails > 0 ? (
											<span className="rounded-full bg-accent px-1.5 py-0.5 text-xs font-semibold text-accent-contrast">
												{mb.unreadEmails}
											</span>
										) : null}
									</button>
								))
							)}
						</div>
					</SurfaceCard>

					<SurfaceCard title="View">
						<div className="space-y-1">
							<button
								onClick={() => {
									setActiveTab('stored');
									setSelectedEmail(null);
								}}
								className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-sm transition ${
									activeTab === 'stored'
										? 'bg-accent text-accent-contrast font-semibold'
										: 'text-secondary hover:bg-surface-2 hover:text-primary'
								}`}
							>
								<Archive className="h-4 w-4" />
								<span>CRM Records</span>
							</button>
						</div>
					</SurfaceCard>
				</div>

				{/* Main content */}
				<div className="grid gap-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
					{/* Email list */}
					<SurfaceCard
						title={
							activeTab === 'live'
								? `${selectedFolder.charAt(0).toUpperCase() + selectedFolder.slice(1)}`
								: 'CRM Linked Emails'
						}
					>
						<div className="mb-2 flex items-center justify-between">
							<span className="text-sm text-muted">{currentEmails.length} messages</span>
							<button
								onClick={() =>
									void queryClient.invalidateQueries({
										queryKey: activeTab === 'live' ? ['email-live', selectedFolder] : ['email-stored']
									})
								}
								className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-surface-2 px-3 py-1.5 text-xs text-secondary transition hover:bg-surface-hover"
							>
								<RefreshCw className="h-3 w-3" />
								Refresh
							</button>
						</div>

						<div className="divide-y divide-subtle">
							{isLoading ? (
								<div className="py-8 text-center text-sm text-muted">Loading emails...</div>
							) : currentEmails.length === 0 ? (
								<div className="py-8 text-center text-sm text-muted">No emails found.</div>
							) : (
								currentEmails.map((em, i) => {
									const isSelected = selectedEmail?.id === em.id;
									const isUnread = em.keywords ? !em.keywords['$seen'] : false;
									const rawFrom = em.from ?? em.from_addr;
									let displayFrom: string;
									if (Array.isArray(rawFrom)) {
										const addr = rawFrom[0] as { name?: string; email?: string };
										displayFrom = addr?.name || addr?.email || 'Unknown';
									} else if (typeof rawFrom === 'object' && rawFrom !== null) {
										const addr = rawFrom as { name?: string; email?: string };
										displayFrom = addr.name || addr.email || 'Unknown';
									} else {
										displayFrom = extractDisplayName(rawFrom as string | undefined);
									}

									return (
										<button
											key={em.id ?? i}
											onClick={() => setSelectedEmail(em)}
											className={`flex w-full flex-col gap-0.5 rounded-xl px-2.5 py-2 text-left transition ${
												isSelected
													? 'bg-accent/10 border border-accent/30'
													: 'hover:bg-surface-2'
											}`}
										>
											<div className="flex items-start justify-between gap-2">
												<span
													className={`truncate text-sm ${isUnread ? 'font-semibold text-primary' : 'text-secondary'}`}
												>
													{displayFrom}
												</span>
												<span className="shrink-0 text-xs text-muted">
													{formatDate(em.receivedAt ?? em.date)}
												</span>
											</div>
											<div className="flex items-center gap-2">
												{isUnread ? (
													<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
												) : (
													<MailOpen className="h-3 w-3 shrink-0 text-slate-600" />
												)}
												<span className="truncate text-xs text-muted">{em.subject ?? '(no subject)'}</span>
											</div>
										</button>
									);
								})
							)}
						</div>
					</SurfaceCard>

					{/* Email detail */}
					<SurfaceCard title={selectedEmail ? 'Message' : 'Select a message'}>
						{selectedEmail ? (
							<div className="space-y-3">
								<div className="space-y-0.5">
									<h2 className="text-base font-semibold text-primary">
										{selectedEmail.subject ?? '(no subject)'}
									</h2>
									<div className="space-y-0.5 text-sm text-muted">
										<p>
											<span className="text-muted">From: </span>
											{(() => {
												const f = selectedEmail.from ?? selectedEmail.from_addr;
												if (typeof f === 'object' && f !== null && !Array.isArray(f)) {
													const addr = f as { name?: string; email?: string };
													return `${addr.name ?? ''} <${addr.email ?? ''}>`.trim();
												}
												if (Array.isArray(f)) {
													const addr = f[0] as { name?: string; email?: string };
													return `${addr.name ?? ''} <${addr.email ?? ''}>`.trim();
												}
												return String(f ?? 'Unknown');
											})()}
										</p>
										<p>
											<span className="text-muted">Date: </span>
											{selectedEmail.receivedAt
												? new Date(selectedEmail.receivedAt).toLocaleString()
												: selectedEmail.date
												? new Date(selectedEmail.date).toLocaleString()
												: 'Unknown'}
										</p>
									</div>
								</div>

								<div className="flex gap-2">
									<button
										onClick={() => setReplyOpen(true)}
										className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-xs text-secondary transition hover:bg-surface-hover"
									>
										<Reply className="h-3.5 w-3.5" />
										Reply
									</button>
								</div>

								<div className="rounded-xl border border-subtle bg-canvas/50 p-3">
									{selectedEmail.bodyValues ? (
										(() => {
											const htmlPart = selectedEmail.htmlBody?.[0]?.partId;
											const textPart = selectedEmail.textBody?.[0]?.partId;
											if (htmlPart && selectedEmail.bodyValues[htmlPart]) {
												return (
													<iframe
														className="h-64 w-full rounded-xl bg-white"
														srcDoc={selectedEmail.bodyValues[htmlPart].value}
														sandbox="allow-same-origin"
													/>
												);
											}
											if (textPart && selectedEmail.bodyValues[textPart]) {
												return (
													<pre className="whitespace-pre-wrap text-sm text-secondary">
														{selectedEmail.bodyValues[textPart].value}
													</pre>
												);
											}
											return <p className="text-sm text-muted">(No body)</p>;
										})()
									) : selectedEmail.body_html ? (
										<iframe
											className="h-64 w-full rounded-xl bg-white"
											srcDoc={selectedEmail.body_html}
											sandbox="allow-same-origin"
										/>
									) : selectedEmail.body_text ? (
										<pre className="whitespace-pre-wrap text-sm text-secondary">
											{selectedEmail.body_text}
										</pre>
									) : (
										<p className="text-sm text-muted">(No body)</p>
									)}
								</div>
							</div>
						) : (
							<div className="flex flex-col items-center justify-center gap-2 py-10 text-muted">
								<Mail className="h-8 w-8" />
								<p className="text-sm">Select a message to read it</p>
							</div>
						)}
					</SurfaceCard>
				</div>
			</div>

			{/* Compose / Reply modal */}
			{(composeOpen || replyOpen) ? (
				<div className="fixed inset-0 z-50 flex items-end justify-end bg-canvas/40 p-4 backdrop-blur-sm">
					<div className="w-full max-w-xl rounded-2xl border border-subtle bg-surface-2 shadow-panel">
						<div className="flex items-center justify-between border-b border-subtle px-4 py-3">
							<h3 className="text-lg font-semibold text-primary">
								{replyOpen ? 'Reply' : 'New Message'}
							</h3>
							<button
								onClick={() => {
									setComposeOpen(false);
									setReplyOpen(false);
								}}
								className="rounded-xl border border-subtle p-2 text-secondary hover:bg-surface-2"
							>
								<X className="h-4 w-4" />
							</button>
						</div>
						<div className="space-y-2 p-4">
							<input
								value={compose.to}
								onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
								placeholder="To (comma separated)"
								className="w-full rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
							/>
							<input
								value={compose.cc}
								onChange={(e) => setCompose((c) => ({ ...c, cc: e.target.value }))}
								placeholder="CC (optional)"
								className="w-full rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
							/>
							<input
								value={compose.subject}
								onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
								placeholder="Subject"
								className="w-full rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
							/>
							<textarea
								value={compose.bodyText}
								onChange={(e) => setCompose((c) => ({ ...c, bodyText: e.target.value }))}
								placeholder="Message body..."
								rows={6}
								className="w-full rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
							/>
							<div className="flex justify-end gap-2">
								<button
									onClick={() => {
										setComposeOpen(false);
										setReplyOpen(false);
									}}
									className="rounded-xl border border-subtle px-3 py-2 text-sm text-secondary hover:bg-surface-2"
								>
									Discard
								</button>
								<button
									onClick={handleSend}
									disabled={sendMutation.isPending}
									className="inline-flex items-center gap-1.5 rounded-xl bg-accent px-3 py-2 text-sm font-semibold text-accent-contrast transition hover:opacity-90 disabled:opacity-60"
								>
									<Send className="h-3.5 w-3.5" />
									{sendMutation.isPending ? 'Sending...' : 'Send'}
								</button>
							</div>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}
